import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Select } from '../../../components/idsk';
import type { FieldDef } from '../../../form-engine';
import { text } from '../../../form-engine/types';
import { PALETTE, PALETTE_GROUPS, allowedIn, groupTargets, type PaletteItem, type PaletteTarget } from './palette';
import { endBuilderDrag, startPaletteDrag, useBuilderDrag } from './builderDrag';
import { PROPERTIES_HEADING } from './FieldEditor';

/** Targets of the palette: the end of the form and every (editable) section and repeating group. */
export function usePaletteTargets(fields: FieldDef[], editable: (f: FieldDef) => boolean = () => true): PaletteTarget[] {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage === 'en' ? 'en' : 'sk';
  return [
    { key: null, type: null, label: t('builder.palette.targetEnd') },
    ...groupTargets(fields, editable).map((f) => ({
      key: f.key,
      type: f.type,
      label: t(f.type === 'SECTION' ? 'builder.palette.targetSection' : 'builder.palette.targetGroup', { label: text(f.label, lang) }),
    })),
  ];
}

export interface ControlPaletteProps {
  /** id prefix (several palettes never share a page, but tests render them side by side) */
  id?: string;
  targets: PaletteTarget[];
  target: string | null;
  onTargetChange: (key: string | null) => void;
  /** Route of the editor of a new control of this palette item in the target. */
  itemPath: (item: PaletteItem, parentKey: string | null) => string;
  /** Heading of the palette; the page heading when the palette is a page of its own. */
  heading?: React.ReactNode;
  /** The controls can be dragged into the field list beside the palette (its `dropPath`). */
  draggable?: boolean;
}

/**
 * The controls palette of the form builder: sections and every field type, grouped, each a link to the editor of a
 * new control. A select chooses where the control is added (end of the form, or a section / repeating group); the
 * controls that cannot go there (a section in a section, …) are left out. Beside a field list the controls can also
 * be dragged to their place in the form; the links stay the way that needs no dragging (WCAG 2.5.7) and the only way
 * for the keyboard.
 */
export function ControlPalette({ id = 'palette', targets, target, onTargetChange, itemPath, heading, draggable = false }: ControlPaletteProps) {
  const { t } = useTranslation();
  const drag = useBuilderDrag();
  const parentType = targets.find((x) => x.key === target)?.type ?? null;
  const items = PALETTE.filter((i) => allowedIn(i.type, parentType));
  return (
    <section className={`app-palette${draggable ? ' app-palette--sticky' : ''}`} aria-labelledby={`${id}-heading`}>
      {heading ?? (
        <h2 className="govuk-heading-m" id={`${id}-heading`}>
          {t('builder.palette.heading')}
        </h2>
      )}
      {draggable && <p className="govuk-hint govuk-!-font-size-16">{t(targets.length > 1 ? 'builder.palette.dragHint' : 'builder.palette.dragHintEnd')}</p>}
      {targets.length > 1 && (
        <Select
          id={`${id}-target`}
          label={t('builder.palette.target')}
          labelClassName="govuk-label--s"
          options={targets.map((x) => ({ value: x.key ?? '', label: x.label }))}
          value={target ?? ''}
          onChange={(e) => onTargetChange(e.target.value || null)}
          width="full"
        />
      )}
      {PALETTE_GROUPS.map((group) => {
        const list = items.filter((i) => i.group === group);
        if (list.length === 0) return null;
        return (
          <Fragment key={group}>
            <h3 className="govuk-heading-s app-palette__group" id={`${id}-${group}`}>
              {t(`builder.palette.groups.${group}`)}
            </h3>
            <ul className="govuk-list app-palette__list" aria-labelledby={`${id}-${group}`}>
              {list.map((item) => (
                // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- dragging is a pointer extra; the link is the way without it (WCAG 2.5.7)
                <li
                  key={item.id}
                  className={`app-palette__item${draggable ? ' app-palette__item--draggable' : ''}${drag?.kind === 'palette' && drag.item.id === item.id ? ' app-palette__item--dragging' : ''}`}
                  draggable={draggable || undefined}
                  onDragStart={draggable ? (e) => startPaletteDrag(e, item) : undefined}
                  onDragEnd={draggable ? endBuilderDrag : undefined}
                >
                  {draggable && (
                    <svg className="app-palette__grip" aria-hidden="true" focusable="false" viewBox="0 0 10 16" width="10" height="16">
                      {[2, 8, 14].flatMap((y) => [2, 8].map((x) => <circle key={`${x}-${y}`} cx={x} cy={y} r="1.6" />))}
                    </svg>
                  )}
                  <span className="app-palette__body">
                    <Link className="govuk-link app-palette__link" to={itemPath(item, target)} state={{ focus: PROPERTIES_HEADING }} aria-describedby={`${id}-${item.id}-hint`} draggable={false}>
                      {t(`builder.palette.items.${item.id}.name`)}
                    </Link>
                    <span className="govuk-hint govuk-!-font-size-16 app-palette__hint" id={`${id}-${item.id}-hint`}>
                      {t(`builder.palette.items.${item.id}.description`)}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </Fragment>
        );
      })}
    </section>
  );
}
